import { Card } from '../ui/card';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ClinicianSelect from '../ClinicianSelect';

const CLINIC_STATUSES = ['Open', 'Limited', 'Closed'];
const EVAL_POLICIES = ['In Clinic Only', 'In Clinic & Outside', 'Outside Only'];

/**
 * Clinic configuration form + scheduling rules & notes.
 *
 * Bubbles all edits back through parent-supplied handlers so the parent
 * remains the single source of truth for the live `clinic` object.
 */
export default function ClinicConfigPanel({
  clinic, setClinic, canEdit, patchClinic,
  setup, setSetup,
  bilingualClinicians, clinicClinicianNames,
  notesDraft, setNotesDraft,
}) {
  return (
    <div className="grid lg:grid-cols-2 gap-6">
      {/* Configuration */}
      <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="clinic-config">
        <h3 className="font-serif text-xl text-[#2D2833] mb-4">Clinic configuration</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-[#685B72]">Speech Evals Open</Label>
            <Select value={clinic.speech_evals_open ? 'Yes' : 'No'} onValueChange={v => canEdit && patchClinic({ speech_evals_open: v === 'Yes' })} disabled={!canEdit}>
              <SelectTrigger data-testid="toggle-speech-evals" className={`rounded-xl mt-1 font-semibold ${clinic.speech_evals_open ? 'bg-[#839E97]/20 text-[#839E97] border-[#839E97]/40' : 'bg-[#8A3A3A]/15 text-[#8A3A3A] border-[#8A3A3A]/40'}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[#685B72]">Feeding Evals Open</Label>
            <Select value={clinic.feeding_evals_open ? 'Yes' : 'No'} onValueChange={v => canEdit && patchClinic({ feeding_evals_open: v === 'Yes' })} disabled={!canEdit}>
              <SelectTrigger data-testid="toggle-feeding-evals" className={`rounded-xl mt-1 font-semibold ${clinic.feeding_evals_open ? 'bg-[#839E97]/20 text-[#839E97] border-[#839E97]/40' : 'bg-[#8A3A3A]/15 text-[#8A3A3A] border-[#8A3A3A]/40'}`}><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="Yes">Yes</SelectItem><SelectItem value="No">No</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="col-span-2 grid grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-[#685B72]">Bilingual Therapy</Label>
              <Select value={clinic.bilingual_therapy || ''} onValueChange={v => canEdit && patchClinic({ bilingual_therapy: v })} disabled={!canEdit}>
                <SelectTrigger className={`rounded-xl mt-1 font-semibold ${
                  (clinic.bilingual_therapy || '').startsWith('Yes') ? 'bg-[#839E97]/20 text-[#839E97] border-[#839E97]/40'
                  : clinic.bilingual_therapy === 'No' ? 'bg-[#8A3A3A]/15 text-[#8A3A3A] border-[#8A3A3A]/40'
                  : clinic.bilingual_therapy === 'Check with Office Director' ? 'bg-[#B08A3A]/20 text-[#B08A3A] border-[#B08A3A]/40'
                  : ''
                }`} data-testid="field-bilingual-therapy"><SelectValue placeholder="Select…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Yes">Yes</SelectItem>
                  <SelectItem value="No">No</SelectItem>
                  <SelectItem value="Yes — treatment only">Yes — treatment only</SelectItem>
                  <SelectItem value="Check with Office Director">Check with Office Director</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#685B72]">Bilingual Evaluator</Label>
              <Select value={clinic.bilingual_eval || ''} onValueChange={v => canEdit && patchClinic({ bilingual_eval: v })} disabled={!canEdit}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="field-bilingual-eval"><SelectValue placeholder="Select bilingual clinician…" /></SelectTrigger>
                <SelectContent>
                  {bilingualClinicians.length === 0 && <SelectItem value="__none__" disabled>No bilingual clinicians assigned to {clinic.name}</SelectItem>}
                  {bilingualClinicians.map(c => <SelectItem key={c.clinician_id} value={c.name}>{c.name} · {c.role}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#685B72]">Priority Evaluator <span className="text-[#685B72]/70">({clinic.name}-assigned only)</span></Label>
              <ClinicianSelect value={clinic.priority_evaluator || ''} setup={setup} setSetup={setSetup} filterNames={clinicClinicianNames} onSelect={v => canEdit && patchClinic({ priority_evaluator: v })} disabled={!canEdit} testId="field-priority-eval" />
            </div>
            <div>
              <Label className="text-xs text-[#685B72]">Max Evals / Week</Label>
              <Input type="number" value={clinic.max_evals_per_week || 0} onChange={e => setClinic({ ...clinic, max_evals_per_week: +e.target.value })} onBlur={e => canEdit && patchClinic({ max_evals_per_week: +e.target.value })} disabled={!canEdit} className="rounded-xl mt-1" data-testid="field-max-evals" />
            </div>
            <div>
              <Label className="text-xs text-[#685B72]">Eval Location Policy</Label>
              <Select value={clinic.eval_location_policy || 'In Clinic Only'} onValueChange={v => canEdit && patchClinic({ eval_location_policy: v })} disabled={!canEdit}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="field-eval-policy"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {EVAL_POLICIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#685B72]">Clinic Status</Label>
              <Select value={clinic.clinic_status || 'Open'} onValueChange={v => canEdit && patchClinic({ clinic_status: v })} disabled={!canEdit}>
                <SelectTrigger className={`rounded-xl mt-1 font-semibold ${clinic.clinic_status === 'Closed' ? 'bg-[#8A3A3A]/15 text-[#8A3A3A]' : clinic.clinic_status === 'Limited' ? 'bg-[#B08A3A]/20 text-[#B08A3A]' : 'bg-[#839E97]/15 text-[#839E97]'}`} data-testid="field-clinic-status"><SelectValue /></SelectTrigger>
                <SelectContent>{CLINIC_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        </div>
      </Card>

      {/* Rules & Notes */}
      <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="clinic-rules">
        <h3 className="font-serif text-xl text-[#2D2833] mb-3">Scheduling rules & notes</h3>
        <Label className="text-xs text-[#685B72]">Rules (one per line)</Label>
        <Textarea
          rows={5}
          value={(clinic.rules || []).join('\n')}
          onChange={e => setClinic({ ...clinic, rules: e.target.value.split('\n') })}
          onBlur={e => canEdit && patchClinic({ rules: e.target.value.split('\n').map(l => l.trim()).filter(Boolean) })}
          disabled={!canEdit}
          className="rounded-xl mt-1 font-mono text-xs"
          data-testid="clinic-rules-textarea"
          placeholder="• Priority Evaluator handles bilingual clients&#10;• Feeding evals only on Tuesdays"
        />
        <Label className="text-xs text-[#685B72] mt-4 block">Additional notes</Label>
        <Textarea rows={3} value={notesDraft} onChange={e => setNotesDraft(e.target.value)} disabled={!canEdit} className="rounded-xl mt-1" data-testid="clinic-notes" placeholder="Holiday hours, staffing changes, special constraints…" />
        {canEdit && (
          <div className="mt-3 flex justify-end">
            <Button onClick={() => patchClinic({ notes: notesDraft })} className="bg-[#685B72] hover:bg-[#52475A] text-white rounded-xl" data-testid="save-clinic-notes">Save Notes</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
